import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import moment from "moment"
import { GetUserapiAdminTestsGetTestByIdByTestIdApiResponse } from "../../app/services/meseeknaApi"
import { Choice, ChoiceType } from "../simulation-decisions/types"
import { Event, Information, Simulation, SimulationState, Step } from "./types"
import {
  arrayHasId,
  convertToSeconds,
  extractChoices,
  extractEvents,
  extractInformation,
  simContents,
} from "./utils"

const initialState: SimulationState = {
  setupDone: false,
  simId: null,
  duration: "",
  events: [] as Event[],
  nextEvent: null,
  pastEvents: [] as Event[],
  messages: [] as Event[],
  secondsElapsed: 0,
  timer: null,
  information: [] as Information[],
  choices: [] as Information[],
  decisions: [] as Choice[],
  plans: [] as Choice[],
  plansEnacted: [] as Choice[],
  searches: [] as Choice[],
  simulationAboutText: "",
  timerAsCountdown: false,
  plansForGlobal: [] as Choice[],
  steps: [
    { name: "Tutorial", isActive: true },
    { name: "Scenario", isActive: false },
    { name: "Simulation", isActive: false },
    { name: "Finish", isActive: false },
  ] as Step[],
  isMessagesLoaded: true,
  emergencyStart: "",
  emergencyEnd: "",
} as SimulationState

export const simulationSlice = createSlice({
  name: "simulationSlice",
  initialState,
  reducers: {
    setupSimulation(
      state,
      action: PayloadAction<GetUserapiAdminTestsGetTestByIdByTestIdApiResponse>,
    ) {
      if (state.setupDone) {
        console.debug("setup already done")
        // TODO: sometimes the duration gets unset, not sure why
        // and this is hacky but 🤷
        if (state.duration === "") {
          state.duration = simContents(action.payload)["length"] ?? ""
        }
        return
      }

      console.debug("setting up simulation", action.payload)
      if (action.payload === undefined) {
        state.events = [] as Event[]
        return
      }
      const simData = (action.payload.data ?? [null])[0]
      if (simData === null) {
        state.events = [] as Event[]
        return
      }
      state.testData = (action.payload.data ?? []) as Simulation[]
      state.timerAsCountdown = false
      console.debug("setting up events")
      const contents = simContents(action.payload)
      state.simId = simData.id ?? null
      state.duration = contents["length"] ?? ""
      state.events = extractEvents(action.payload)
      state.information = extractInformation(action.payload)
      state.choices = extractChoices(action.payload)
      state.simulationAboutText = simData.manualDescription ?? ""
      state.emergencyStart = contents.emergencyStart
      state.emergencyEnd = contents.emergencyEnd
      console.debug("events", state.events)
      for (const event of state.events) {
        if (convertToSeconds(event.elapsedTime ?? "") <= state.secondsElapsed) {
          if (event.id && !arrayHasId(state.pastEvents, event.id)) {
            state.pastEvents.push(event)
          }
        }
      }
      state.setupDone = true
    },
    incrementSecondsElapsed(state) {
      state.secondsElapsed += 1
    },
    setTimer(state, action: PayloadAction<NodeJS.Timeout | null>) {
      state.timer = action.payload
    },
    setSecondsElapsed(state, action: PayloadAction<number>) {
      state.secondsElapsed = action.payload
    },
    setTimerAsCountdown(state, action: PayloadAction<boolean>) {
      state.timerAsCountdown = action.payload
    },
    removePlan(state, action: PayloadAction<Choice>) {
      const filteredPlans = state.plans.filter((plan) => {
        return (
          plan.item.id !== action.payload.item.id ||
          plan.time !== action.payload.time
        )
      })
      state.plans = filteredPlans
    },
    createTimerWithStartTime(state, action: PayloadAction<string>) {
      const secondsElapsed = convertToSeconds(action.payload)
      state.secondsElapsed = secondsElapsed
    },
    createTimerWithElapsedTime(state, action: PayloadAction<number>) {
      state.secondsElapsed = action.payload
    },
    transitionNextToPast(state) {
      if (state.nextEvent === null) return
      if (
        state.nextEvent.id &&
        !arrayHasId(state.pastEvents, state.nextEvent.id)
      ) {
        state.pastEvents.push(state.nextEvent)
        state.nextEvent = null
      }
      if (state.events.length === 0) return
      state.nextEvent = state.events[0]
      state.events.shift()
    },
    addMessage(state, action: PayloadAction<Event>) {
      // Generate a unique ID based on the current messages length + 1
      const newMessage = {
        ...action.payload,
        id: state.messages.length + 1
      }
      state.messages.push(newMessage)
      state.isMessagesLoaded = true
    },
    addChoice(state, action: PayloadAction<Choice>) {
      const { choiceType } = action.payload

      switch (choiceType) {
        case ChoiceType.decision:
          state.decisions.push(action.payload)
          break
        case ChoiceType.plan:
          state.plans.push(action.payload)
          state.plansForGlobal.push(action.payload)
          break
        case ChoiceType.search:
          state.searches.push(action.payload)
          break
        case ChoiceType.eplan:
        case ChoiceType.cplan:
          // Do nothing, shouldn't even be here
          break
        default:
          console.error("unknown choice type", choiceType)
          break
      }

      state.isMessagesLoaded = true
    },
    addEnactedPlan(state, action: PayloadAction<Choice>) {
      state.plansEnacted.push(action.payload)
    },
    updateChoice(state, action: PayloadAction<Choice[]>) {
      state.plans = action.payload
    },
    setMessages(state, action: PayloadAction<Event[]>) {
      state.messages = action.payload
    },
    setEvents(state, action: PayloadAction<Event[]>) {
      state.events = action.payload
    },
    setPastEvents(state, action: PayloadAction<Event[]>) {
      state.pastEvents = action.payload
    },
    setNextEvent(state, action: PayloadAction<Event | null>) {
      state.nextEvent = action.payload
    },
    setSimulationAboutText(state, action: PayloadAction<string>) {
      state.simulationAboutText = action.payload
    },
    setDuration(state, action: PayloadAction<string>) {
      state.duration = action.payload
    },
    destroyTimer(state) {
      // technically should be in thunk?
      if (state.timer) {
        clearInterval(state.timer)
        state.timer = null
      }
    },
    resetSimulation() {
      return initialState
    },
    setPlans(state, action: PayloadAction<Event[]>) {
      const cplanEvents = action.payload
        .filter((event) => event.mode === "plan")
        .map((event) => ({
          choiceType: event.mode,
          item: {
            actionSentence: event.description,
            icon: null,
            id: event.id,
            infoSearchCategory: null,
            isInfoSearch: 0,
            order: event.order ?? 0,
            planSentence: event.description,
            text: event.title,
          },
          time: moment(event.dateTime).valueOf(),
        }))

      state.plans = cplanEvents as Choice[]
    },
    changeSteps(state, action: PayloadAction<number>) {
      const payload = action.payload
      state.steps = state.steps.map((step, index) => ({
        ...step,
        isActive: index === payload,
      }))
    },
    setLoadingMessages(state, action: PayloadAction<boolean>) {
      state.isMessagesLoaded = action.payload
    },
    setInformation(state, action: PayloadAction<Information[]>) {
      state.information = action.payload
    },
    setChoices(state, action: PayloadAction<Information[]>) {
      state.choices = action.payload
    },
  },
})

export const simulationReducer = persistReducer(
  {
    key: "msk:simulation",
    storage,
    whitelist: [
      "setupDone",
      "simId",
      "events",
      "pastEvents",
      "nextEvent",
      "messages",
      "secondsElapsed",
      "information",
      "choices",
      "decisions",
      "plans",
      "plansEnacted",
      "searches",
      "simulationAboutText",
      "duration",
      "steps",
      "isMessagesLoaded",
      "emergencyStart",
      "emergencyEnd",
    ],
  },
  simulationSlice.reducer,
)

export const {
  setupSimulation,
  incrementSecondsElapsed,
  setTimer,
  setSecondsElapsed,
  createTimerWithStartTime,
  createTimerWithElapsedTime,
  transitionNextToPast,
  addMessage,
  addChoice,
  addEnactedPlan,
  updateChoice,
  setMessages,
  setEvents,
  setPastEvents,
  setNextEvent,
  setSimulationAboutText,
  setDuration,
  setTimerAsCountdown,
  removePlan,
  destroyTimer,
  resetSimulation,
  setLoadingMessages,
  setInformation,
  setChoices,
} = simulationSlice.actions

export default simulationSlice.reducer
